import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { AuthContext, type AuthStatus, type AuthUser } from './context'
import { setServerCsrfToken, serverMutationHeaders } from './serverSession'

interface SessionPayload {
  authenticated: true
  csrfToken: string
  user: AuthUser
}

const IS_MOCK = import.meta.env.VITE_MOCK_DATA === 'true'

export function ServerSessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>(
    IS_MOCK ? 'authenticated' : 'loading',
  )
  const [user, setUser] = useState<AuthUser | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (IS_MOCK) return
    try {
      const response = await fetch('/api/v1/session', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      })
      if (response.status === 401) {
        setServerCsrfToken(null)
        setUser(null)
        setStatus('unauthenticated')
        return
      }
      if (!response.ok) throw new Error(`Session check failed (${response.status})`)
      const payload = await response.json() as SessionPayload
      setServerCsrfToken(payload.csrfToken)
      setUser(payload.user)
      setError(null)
      setStatus('authenticated')
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
      setStatus('error')
    }
  }, [])

  useEffect(() => {
    if (IS_MOCK) return
    void load()
    return () => setServerCsrfToken(null)
  }, [load])

  const login = useCallback(async () => {
    const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`
    window.location.assign(`/auth/login?return_to=${encodeURIComponent(returnTo)}`)
  }, [])

  const logout = useCallback(async () => {
    const response = await fetch('/auth/logout', {
      method: 'POST',
      credentials: 'same-origin',
      headers: serverMutationHeaders(),
    })
    if (!response.ok && response.status !== 401) {
      throw new Error(`Logout failed (${response.status})`)
    }
    setServerCsrfToken(null)
    setUser(null)
    setStatus('unauthenticated')
  }, [])

  return (
    <AuthContext.Provider
      value={{
        status,
        user,
        accessToken: null,
        error,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
